import React, { useEffect, useRef, useState } from "react";
import { LobbyRefreshData } from "../../../../../types/lobby";

import "./VehicleStatusBox.scss";
import carAuto from "../../../../../assets/imgs/lobby/car_auto.svg";
import carManual from "../../../../../assets/imgs/lobby/car_manual.svg";
import carRemote from "../../../../../assets/imgs/lobby/car_remote.svg";
import L1img from "../../../../../assets/imgs/lobby/modem_L1.svg";
import L2img from "../../../../../assets/imgs/lobby/modem_L2.svg";
import L3img from "../../../../../assets/imgs/lobby/modem_L3.svg";
import dlyimg from "../../../../../assets/imgs/lobby/modem_Delay.svg";
import audimg from "../../../../../assets/imgs/lobby/audio_alive.svg";

interface VehicleStatusBoxProps {
  vehicleCommunicationCode: string;
  vehicleNo: string;
  vehicleLabel: string;
  refreshData: LobbyRefreshData;
  isMqttConnected: boolean;
}

const VehicleStatusBox: React.FC<VehicleStatusBoxProps> = ({
  vehicleCommunicationCode,
  vehicleNo,
  vehicleLabel,
  refreshData,
  isMqttConnected,
}) => {
  const prevModemId = useRef<number | null>(null);
  const prevAudioId = useRef<number | null>(null);
  const [showModem, setShowModem] = useState(false);
  const [showModemLabel, setShowModemLabel] = useState(false);
  const [showAudio, setShowAudio] = useState(false);
  const [antennaLevel, setAntennaLevel] = useState(1);
  const isConnectRef = useRef(1);
  const lastReceivedTimeRef = useRef<number | null>(null);

  const driveMode = refreshData?.drive?.drive_mode ?? null;
  const driveShift = refreshData?.drive?.shift ?? null;
  const velocity = refreshData?.drive?.velocity ?? null;
  const modemRaw = {
    line1: refreshData?.modem?.line1_bitrate ?? null,
    line2: refreshData?.modem?.line2_bitrate ?? null,
    line3: refreshData?.modem?.line3_bitrate ?? null,
  } as const;
  const delay = refreshData?.modem?.delay_time ?? "--";
  const audioAlive = refreshData?.audio?.audio_alive ?? "--";

  const formatKbpsToMbps = (b: number | undefined | null) => {
    if (!b) return "--"; // 0, undefined, or null
    const scaled = (b / 1000) * 10;
    const roundedUp = Math.ceil(scaled) / 10;
    return `${roundedUp.toFixed(1)}`;
  };

  const line1 = formatKbpsToMbps(modemRaw.line1);
  const line2 = formatKbpsToMbps(modemRaw.line2);
  const line3 = formatKbpsToMbps(modemRaw.line3);

  const driveImgSrc = (() => {
    switch (driveMode) {
      case 1:
        return carAuto;
      case 3:
        return carManual;
      case 8:
        return carRemote;
      default:
        return;
    }
  })();

  const relevantCr =
    Array.isArray(refreshData?.cr) &&
    refreshData.cr
      .filter((cr) => cr.confirmation_response_cd !== "2")
      .sort((a, b) => {
        if (a.priority !== b.priority) {
          return b.priority - a.priority;
        }
        return (
          new Date(b.occurrence_datetime).getTime() -
          new Date(a.occurrence_datetime).getTime()
        );
      })[0];

  // 以下アンテナの表示処理
  useEffect(() => {
    if (refreshData?.drive?.monitor_f !== "0") {
      lastReceivedTimeRef.current = Date.now();
    }
  }, [refreshData?.drive?.vehicle_info_cardrive_id]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (navigator.onLine === false && isConnectRef.current === 1) {
        isConnectRef.current = 0;
        window.alert("CAUTION! 監視センターに通信断が発生しています。");
      }
      if (navigator.onLine === true) {
        isConnectRef.current = 1;
      }

      if (!isMqttConnected) {
        setAntennaLevel(0);
      } else if (!lastReceivedTimeRef.current) {
        setAntennaLevel(1);
      } else {
        const elapsed = Date.now() - lastReceivedTimeRef.current;
        if (elapsed >= 2000) setAntennaLevel(1);
        else if (elapsed >= 200) setAntennaLevel(2);
        else setAntennaLevel(3);
      }
    }, 200);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (prevModemId.current === null) {
      prevModemId.current = refreshData?.modem?.vehicle_info_modem_id ?? null;
      return;
    }
    if (prevModemId.current !== refreshData?.modem?.vehicle_info_modem_id) {
      setShowModem(true);
      setShowModemLabel(true);
      const timer = setTimeout(() => setShowModem(false), 2000);
      prevModemId.current = refreshData?.modem?.vehicle_info_modem_id ?? null;
      return () => clearTimeout(timer);
    }
  }, [refreshData?.modem?.vehicle_info_modem_id]);

  useEffect(() => {
    if (prevAudioId.current === null) {
      prevAudioId.current =
        refreshData?.audio?.vehicle_info_audio_alive_id ?? null;
      return;
    }
    if (
      prevAudioId.current !== refreshData?.audio?.vehicle_info_audio_alive_id
    ) {
      setShowAudio(true);
      prevAudioId.current =
        refreshData?.audio?.vehicle_info_audio_alive_id ?? null;
    }
  }, [refreshData?.audio?.vehicle_info_audio_alive_id]);

  return (
    <div
      className={`statusArea
                  ${
                    Array.isArray(refreshData?.monitor) &&
                    refreshData?.monitor[0]?.is_selecting === "1"
                      ? "isMonitoring"
                      : Array.isArray(refreshData?.selected_monitor) &&
                          refreshData?.selected_monitor[0]
                        ? "isMonitoring"
                        : ""
                  }
                  ${
                    relevantCr && typeof relevantCr === "object"
                      ? relevantCr.item_color
                      : ""
                  }
                `}
      id={`car-${vehicleCommunicationCode}`}
    >
      {Array.isArray(refreshData?.selected_monitor) &&
        refreshData?.selected_monitor[0] && (
          <div className="monitorStatus">
            <div className="monitorId">
              {refreshData?.selected_monitor[0].monitor_console_no}
            </div>
            <div>監視中</div>
          </div>
        )}
      <div
        className={`vehicleStatus ${
          relevantCr &&
          typeof relevantCr === "object" &&
          relevantCr.confirmation_response_cd != "2"
            ? relevantCr.item_color
            : ""
        }`}
      >
        <div
          className={`signalArea
                      ${
                        relevantCr &&
                        typeof relevantCr === "object" &&
                        relevantCr.confirmation_response_cd != "2"
                          ? relevantCr.item_color
                          : ""
                      }
                    `}
        >
          <div className="antennaNo">{vehicleNo}</div>
          <div className="antenna">
            {refreshData?.drive?.monitor_f !== "0" ? (
              <>
                <span
                  className={`bar bar1 ${antennaLevel >= 1 ? "active" : ""}`}
                />
                <span
                  className={`bar bar2 ${antennaLevel >= 2 ? "active" : ""}`}
                />
                <span
                  className={`bar bar3 ${antennaLevel >= 3 ? "active" : ""}`}
                />
              </>
            ) : null}
          </div>
        </div>
        <div
          className={`vehicleStatusInner
                      ${
                        relevantCr &&
                        typeof relevantCr === "object" &&
                        relevantCr.confirmation_response_cd != "2"
                          ? relevantCr.item_color
                          : ""
                      }
                    `}
        >
          <div className="statusMain">
            <div className="topArea">
              <div
                className={`vehicleName ${
                  relevantCr &&
                  typeof relevantCr === "object" &&
                  relevantCr.confirmation_response_cd != "2"
                    ? relevantCr.item_color
                    : ""
                }`}
              >
                {vehicleLabel}
              </div>
              {refreshData?.drive?.monitor_f !== "0" && (
                <>
                  <div className="driveImg">
                    {driveImgSrc ? <img src={driveImgSrc} alt="drive" /> : null}
                  </div>
                  {driveShift ? (
                    <div className="driveShift">{driveShift}</div>
                  ) : null}
                  <div className="driveSpeed">
                    {typeof velocity === "number" ? (
                      <>
                        <span className="speedNumber">
                          {velocity === 0 ? "0" : (velocity / 10).toFixed(1)}
                        </span>
                        <span className="speedUnit">km/h</span>
                      </>
                    ) : null}
                  </div>
                  <div className="aliveStatus">
                    <div className="modemWrapper">
                      {showModemLabel ? (
                        <>
                          <div className="modemLine1">
                            <div className="statusValue">
                              {showModem ? line1 : "--"}
                            </div>
                            <img src={L1img} alt="L1" />
                          </div>
                          <div className="modemLine2">
                            <div className="statusValue">
                              {showModem ? line2 : "--"}
                            </div>
                            <img src={L2img} alt="L2" />
                          </div>
                          <div className="modemLine3">
                            <div className="statusValue">
                              {showModem ? line3 : "--"}
                            </div>
                            <img src={L3img} alt="L3" />
                          </div>
                          <div className="delay">
                            <div
                              className={`statusValue ${
                                !showModem ||
                                (showModem &&
                                  typeof delay === "number" &&
                                  delay > 2000)
                                  ? "text-red"
                                  : ""
                              }`}
                            >
                              {showModem && typeof delay === "number"
                                ? delay < 0
                                  ? "-"
                                  : (Math.ceil(delay / 100) / 10).toFixed(1)
                                : "--"}
                            </div>
                            <img src={dlyimg} alt="DLY" />
                          </div>
                        </>
                      ) : null}
                    </div>
                    <div className="audio">
                      {showAudio ? (
                        <>
                          <div
                            className={`statusValue lbyaud ${
                              showAudio &&
                              typeof audioAlive === "number" &&
                              audioAlive !== 0
                                ? "text-red"
                                : ""
                            }`}
                          >
                            {audioAlive}
                          </div>
                          <img src={audimg} alt="AUD" />
                        </>
                      ) : null}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="divider" />
            <div className="bottomArea">
              {refreshData?.drive?.monitor_f === "0" && (
                <div className="noMonitor">監視運用外</div>
              )}
              {relevantCr && relevantCr.confirmation_response_cd != "2" && (
                <div className="crLabel">
                  {relevantCr.confirmation_request_class_name}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleStatusBox;
